import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring, AbsoluteFill } from 'remotion';
import { COLORS, SUBS, SCENES } from '../constants';
import { GlowBackground } from '../components/GlowBackground';
import { AnimatedText } from '../components/AnimatedText';
import { SyncedSubtitles } from '../components/SyncedSubtitles';

const AnimatedBar: React.FC<{
  height: number;
  maxHeight: number;
  delay: number;
  color: string;
  label: string;
}> = ({ height, maxHeight, delay, color, label }) => {
  const frame = useCurrentFrame();
  const f = frame - delay;

  const barHeight = interpolate(f, [0, 30], [0, (height / 100) * maxHeight], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ height: maxHeight, display: 'flex', alignItems: 'flex-end' }}>
        <div
          style={{
            width: 32,
            height: barHeight,
            borderRadius: '5px 5px 0 0',
            background: `linear-gradient(180deg, ${color}, ${color}60)`,
            boxShadow: `0 0 16px ${color}30`,
          }}
        />
      </div>
      <div style={{ fontSize: 10, color: COLORS.gray, fontFamily: 'Inter, system-ui, sans-serif' }}>
        {label}
      </div>
    </div>
  );
};

const KPICard: React.FC<{
  title: string;
  value: string;
  change: string;
  positive: boolean;
  delay: number;
  icon: string;
}> = ({ title, value, change, positive, delay, icon }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - delay;

  if (f < 0) return <div style={{ flex: 1, opacity: 0 }} />;

  const scale = spring({ frame: f, fps, config: { damping: 15, stiffness: 120 } });
  const opacity = interpolate(f, [0, 12], [0, 1], { extrapolateRight: 'clamp' });

  return (
    <div
      style={{
        flex: 1,
        background: `${COLORS.bgLight}CC`,
        border: `1px solid ${COLORS.primary}20`,
        borderRadius: 12,
        padding: '14px 16px',
        opacity,
        transform: `scale(${scale})`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span
          style={{ fontSize: 11, color: COLORS.gray, fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          {title}
        </span>
      </div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 800,
          color: COLORS.white,
          fontFamily: 'Inter, system-ui, sans-serif',
          marginBottom: 2,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: positive ? COLORS.emerald : COLORS.red,
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        {change}
      </div>
    </div>
  );
};

const DUR = SCENES.ANALYTICS.duration;

export const AnalyticsScene: React.FC = () => {
  const frame = useCurrentFrame();

  const fadeIn = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: 'clamp' });
  const fadeOut = interpolate(frame, [DUR - 15, DUR], [1, 0], { extrapolateRight: 'clamp' });

  const barData = [
    { height: 45, label: 'Ene', color: COLORS.primary },
    { height: 60, label: 'Feb', color: COLORS.primary },
    { height: 55, label: 'Mar', color: COLORS.primary },
    { height: 75, label: 'Abr', color: COLORS.primary },
    { height: 65, label: 'May', color: COLORS.primary },
    { height: 80, label: 'Jun', color: COLORS.primaryLight },
    { height: 70, label: 'Jul', color: COLORS.primary },
    { height: 90, label: 'Ago', color: COLORS.primary },
    { height: 85, label: 'Sep', color: COLORS.primary },
    { height: 95, label: 'Oct', color: COLORS.primaryLight },
    { height: 88, label: 'Nov', color: COLORS.primary },
    { height: 100, label: 'Dic', color: COLORS.emerald },
  ];

  const donutProgress = interpolate(frame, [60, 120], [0, 78], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  const leaderReveal = interpolate(frame, [100, 120], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  const leaders = [
    { name: 'María López', revenue: '$482K', rate: '74%', medal: '🥇' },
    { name: 'Carlos Ruiz', revenue: '$356K', rate: '68%', medal: '🥈' },
    { name: 'Ana Torres', revenue: '$298K', rate: '71%', medal: '🥉' },
  ];

  return (
    <AbsoluteFill style={{ opacity: fadeIn * fadeOut }}>
      <GlowBackground variant="default" />

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '0 60px',
          gap: 16,
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <AnimatedText
            text="Analytics en Tiempo Real"
            fontSize={44}
            fontWeight={800}
            mode="scaleIn"
            startFrame={5}
          />
        </div>

        <div style={{ display: 'flex', gap: 14 }}>
          <KPICard icon="💰" title="Revenue" value="$2.4M" change="+18.5%" positive delay={15} />
          <KPICard
            icon="📋"
            title="Cotizaciones"
            value="1,248"
            change="+24.3%"
            positive
            delay={22}
          />
          <KPICard
            icon="🎯"
            title="Ticket Prom."
            value="$1,920"
            change="+12.1%"
            positive
            delay={29}
          />
          <KPICard icon="📈" title="Win Rate" value="68%" change="+5.2%" positive delay={36} />
          <KPICard icon="💹" title="Margen" value="34.2%" change="+3.1%" positive delay={43} />
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          {/* Revenue Chart */}
          <div
            style={{
              flex: 2,
              background: `${COLORS.bgLight}AA`,
              border: `1px solid ${COLORS.primary}15`,
              borderRadius: 14,
              padding: 20,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.white,
                fontFamily: 'Inter, system-ui, sans-serif',
                marginBottom: 14,
              }}
            >
              📊 Revenue Mensual
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
              {barData.map((bar, i) => (
                <AnimatedBar
                  key={bar.label}
                  height={bar.height}
                  maxHeight={180}
                  delay={50 + i * 4}
                  color={bar.color}
                  label={bar.label}
                />
              ))}
            </div>
          </div>

          {/* Win Rate Donut */}
          <div
            style={{
              flex: 0.8,
              background: `${COLORS.bgLight}AA`,
              border: `1px solid ${COLORS.primary}15`,
              borderRadius: 14,
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.white,
                fontFamily: 'Inter, system-ui, sans-serif',
                marginBottom: 10,
                alignSelf: 'flex-start',
              }}
            >
              Win Rate
            </div>
            <svg width={140} height={140} viewBox="0 0 140 140">
              <circle
                cx={70}
                cy={70}
                r={55}
                fill="none"
                stroke={`${COLORS.grayDark}40`}
                strokeWidth={12}
              />
              <circle
                cx={70}
                cy={70}
                r={55}
                fill="none"
                stroke={COLORS.emerald}
                strokeWidth={12}
                strokeLinecap="round"
                strokeDasharray={`${(donutProgress / 100) * 346} 346`}
                transform="rotate(-90 70 70)"
                style={{ filter: `drop-shadow(0 0 6px ${COLORS.emerald}60)` }}
              />
              <text
                x={70}
                y={68}
                textAnchor="middle"
                fill={COLORS.white}
                fontSize={28}
                fontWeight={800}
                fontFamily="Inter, system-ui, sans-serif"
              >
                {Math.floor(donutProgress)}%
              </text>
              <text
                x={70}
                y={88}
                textAnchor="middle"
                fill={COLORS.gray}
                fontSize={10}
                fontFamily="Inter, system-ui, sans-serif"
              >
                Tasa de éxito
              </text>
            </svg>
            <div
              style={{
                marginTop: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                width: '100%',
              }}
            >
              {[
                { label: 'Ganadas', color: COLORS.emerald, pct: '68%' },
                { label: 'Perdidas', color: COLORS.red, pct: '22%' },
                { label: 'Pendientes', color: COLORS.gray, pct: '10%' },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 11,
                    color: COLORS.gray,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  <div
                    style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: item.color }}
                  />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  <span style={{ fontWeight: 600, color: COLORS.white }}>{item.pct}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Leaderboard */}
          <div
            style={{
              flex: 0.9,
              background: `${COLORS.bgLight}AA`,
              border: `1px solid ${COLORS.primary}15`,
              borderRadius: 14,
              padding: 20,
              opacity: leaderReveal,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: COLORS.white,
                fontFamily: 'Inter, system-ui, sans-serif',
                marginBottom: 12,
              }}
            >
              🏆 Top Vendedores
            </div>
            {leaders.map((l, i) => (
              <div
                key={l.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 0',
                  borderTop: i > 0 ? `1px solid ${COLORS.grayDark}20` : 'none',
                }}
              >
                <span style={{ fontSize: 20 }}>{l.medal}</span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: COLORS.white,
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    {l.name}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: COLORS.gray,
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    {l.revenue}
                  </div>
                </div>
                <div
                  style={{
                    padding: '3px 8px',
                    borderRadius: 100,
                    background: `${COLORS.emerald}15`,
                    fontSize: 10,
                    fontWeight: 600,
                    color: COLORS.emerald,
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  {l.rate}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <SyncedSubtitles lines={SUBS.ANALYTICS} />
    </AbsoluteFill>
  );
};
